<template>
  <hs-modal :model-value="true" title="Edit store subscription" size="md" @close="$emit('close')">
    <div class="space-y-4">
      <p class="text-sm">{{ subscription.store_name || `Store #${subscription.store_id}` }}</p>
      <fieldset class="subscription-edit__section">
        <legend class="subscription-edit__heading">Subscription</legend>
        <hs-select v-model="form.plan_id" label="Plan" :options="planOptions" :disabled="saving" />
        <hs-input v-model="form.starts_at" label="Start date and time" type="datetime-local" required :disabled="saving" />
        <p class="text-sm text-gray-500">Changing the plan or start date sets expiry {{ selectedPlan?.billing_period_days }} days after the start. Times use your local timezone.</p>
        <p v-if="expiryPreview" class="text-sm">Expires: {{ expiryPreview }}</p>
      </fieldset>
      <fieldset class="subscription-edit__section">
        <legend class="subscription-edit__heading">Payment details</legend>
        <div class="subscription-edit__payment-row">
          <hs-input v-model="form.payment_amount" label="Payment amount" type="number" :disabled="saving" />
          <hs-input v-model="form.payment_currency" label="Currency" maxlength="3" :disabled="saving" />
        </div>
        <hs-input v-model="form.payment_reference" label="Payment reference" maxlength="128" :disabled="saving" />
        <hs-input v-model="form.paid_at" label="Paid at" type="datetime-local" :disabled="saving" />
      </fieldset>
      <hs-alert v-if="error" tone="danger" :message="error" />
    </div>
    <template #footer>
      <hs-button variant="secondary" :disabled="saving" @click="$emit('close')">Cancel</hs-button>
      <hs-button :disabled="saving || !expiryPreview" @click="save">{{ saving ? 'Saving…' : 'Save changes' }}</hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { HsModal, HsInput, HsSelect, HsButton, HsAlert } from "@/components/ui";
import http from "@/helper/http";

const localDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" :
    new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
};

export default {
  name: "StoreSubscriptionEditModal",
  components: { HsModal, HsInput, HsSelect, HsButton, HsAlert },
  props: {
    subscription: { type: Object, required: true },
    plans: { type: Array, default: () => [] },
  },
  emits: ["close", "saved"],
  data() {
    const sub = this.subscription;
    const form = {
      plan_id: sub.plan_id,
      starts_at: localDate(sub.starts_at),
      payment_amount: sub.payment_amount ?? "",
      payment_currency: sub.payment_currency || "",
      payment_reference: sub.payment_reference || "",
      paid_at: localDate(sub.paid_at),
    };
    return {
      form, original: { ...form },
      saving: false, error: "",
    };
  },
  computed: {
    availablePlans() {
      const plans = this.plans.filter((plan) => plan.is_active);
      if (this.subscription.plan && !plans.some((plan) => plan.id === this.subscription.plan_id)) {
        return [this.subscription.plan, ...plans];
      }
      return plans;
    },
    planOptions() {
      return this.availablePlans.map((plan) => ({ value: plan.id, label: plan.display_name }));
    },
    selectedPlan() {
      return this.availablePlans.find((plan) => plan.id === Number(this.form.plan_id));
    },
    expiryPreview() {
      if (!this.form.starts_at || !this.selectedPlan) return "";
      const start = new Date(this.form.starts_at);
      if (Number.isNaN(start.getTime())) return "";
      const periodChanged = this.form.starts_at !== this.original.starts_at || Number(this.form.plan_id) !== this.original.plan_id;
      const expiry = periodChanged ? new Date(start.getTime() + this.selectedPlan.billing_period_days * 86400000) : new Date(this.subscription.expires_at);
      return expiry.toLocaleString();
    },
  },
  methods: {
    async save() {
      if (this.saving) return;
      this.error = "";

      // Build subscription edits payload
      const editPayload = {};
      const editFields = ["plan_id", "starts_at", "payment_amount", "payment_currency", "payment_reference", "paid_at"];
      for (const field of editFields) {
        if (this.form[field] === this.original[field]) continue;
        editPayload[field] = this.form[field];
      }

      if ("plan_id" in editPayload) editPayload.plan_id = Number(editPayload.plan_id);
      for (const field of ["starts_at", "paid_at"]) {
        if (field in editPayload) {
          const date = new Date(editPayload[field]);
          if (editPayload[field] && Number.isNaN(date.getTime())) {
            this.error = "Enter valid dates.";
            return;
          }
          editPayload[field] = editPayload[field] ? date.toISOString() : null;
        }
      }
      if ("payment_amount" in editPayload) {
        editPayload.payment_amount = editPayload.payment_amount === "" ? null : Number(editPayload.payment_amount);
        if (editPayload.payment_amount !== null && (!Number.isInteger(editPayload.payment_amount) || editPayload.payment_amount < 0)) {
          this.error = "Payment amount must be a non-negative whole number.";
          return;
        }
      }
      if ("payment_currency" in editPayload) editPayload.payment_currency = editPayload.payment_currency.trim().toUpperCase() || null;
      if ("payment_reference" in editPayload) editPayload.payment_reference = editPayload.payment_reference.trim() || null;

      if (!Object.keys(editPayload).length) {
        this.$emit("close");
        return;
      }

      this.saving = true;
      try {
        const { data } = await http.patch(`/v1/admin/subscriptions/stores/${this.subscription.id}`, editPayload);
        this.$emit("saved", data);
      } catch (error) {
        this.error = error?.response?.data?.error?.message || "Unable to update subscription.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.subscription-edit__section {
  display: grid;
  gap: 1rem;
  min-width: 0;
  border: 1px solid var(--hs-border, #e2e8f0);
  border-radius: 0.75rem;
  padding: 1rem;
}

.subscription-edit__heading {
  padding: 0 0.35rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--hs-text, #0f172a);
}

.subscription-edit__payment-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  align-items: start;
  gap: 1rem;
}

.subscription-edit__payment-row > * {
  min-width: 0;
}

</style>
